/**
 * Per-platform UI translation overrides (admin-configured in rocky-admin,
 * served by rocky-api GET /v3/ui-translations/:platformId/:lang as a flat
 * { key: value } map) are merged into vue-i18n with mergeLocaleMessage.
 *
 * Keys are dot paths to a single string of src/i18n/*.json:
 *   "betslip"      -> a top-level string
 *   "errors.sign"  -> the `sign` string inside the `errors` group
 * Each override is turned into a nested object holding ONLY that leaf
 * ({ errors: { sign: "..." } }), so the deep merge changes that one string and
 * leaves every other string of the group untouched.
 *
 * An override is applied only when its path ends on a plain string in the
 * bundle (current locale, or the fallback locale when the current one lacks
 * it). A path that ends on a whole group (e.g. "errors", "cashout") is rejected:
 * merging a string there would replace the group and turn every
 * $t('errors.xxx') into its raw key. Unknown paths, blank / non-string values
 * and prototype-related segments are rejected too.
 *
 * @param {Object} overrides        flat { key: value } from rocky-api
 * @param {Object} localeMessages   bundled messages of the current locale
 * @param {Object} fallbackMessages bundled messages of the fallback locale
 * @returns {{ messages: Object, rejected: string[] }} messages ready for mergeLocaleMessage
 */
const FORBIDDEN_SEGMENTS = ['__proto__', 'prototype', 'constructor'];

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

// Own-property walk only: never resolves through the prototype chain.
const resolvePath = (obj, segments) => {
    let node = obj;
    for (const segment of segments) {
        if (!node || typeof node !== 'object' || !hasOwn(node, segment)) return undefined;
        node = node[segment];
    }
    return node;
};

const setPath = (obj, segments, value) => {
    let node = obj;
    segments.slice(0, -1).forEach((segment) => {
        if (!hasOwn(node, segment) || typeof node[segment] !== 'object' || node[segment] === null) {
            node[segment] = {};
        }
        node = node[segment];
    });
    node[segments[segments.length - 1]] = value;
};

export function buildTranslationOverrideMessages(overrides, localeMessages, fallbackMessages) {
    const messages = {};
    const rejected = [];
    const own = localeMessages || {};
    const fallback = fallbackMessages || {};

    Object.keys(overrides || {}).forEach((key) => {
        const value = overrides[key];
        const segments = String(key).split('.');
        if (
            typeof value !== 'string' || value.trim() === '' ||
            segments.some(s => s === '' || FORBIDDEN_SEGMENTS.includes(s))
        ) {
            rejected.push(key);
            return;
        }

        // 1. Nested path — what vue-i18n resolves first for $t('a.b').
        let target = resolvePath(own, segments);
        if (target === undefined) target = resolvePath(fallback, segments);
        if (typeof target === 'string') {
            setPath(messages, segments, value);
            return;
        }

        // 2. A literal top-level key that itself contains a dot (e.g. "carousel.prev"),
        //    used only when no nested path exists.
        if (target === undefined && segments.length > 1) {
            const literal = hasOwn(own, key) ? own[key] : (hasOwn(fallback, key) ? fallback[key] : undefined);
            if (typeof literal === 'string') {
                messages[key] = value;
                return;
            }
        }

        // A whole group, an unknown key, or a path through a non-object.
        rejected.push(key);
    });

    return { messages, rejected };
}
